import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, Edit3, Save, Trash2, Plus, SortAsc, SortDesc, CheckSquare, Square, BarChart3 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { apiService } from '../services/apiService';

// Helper function to filter out non-selectable categories (fallback categories)
const getSelectableCategories = (categories) => {
  return categories.filter(cat => !cat.startsWith('⚠️'));
};

const RecentItemsModal = ({ onClose, listId, onAddToList }) => {
  const { t } = useLanguage();
  const [recentItems, setRecentItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [editCategory, setEditCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [sortBy, setSortBy] = useState('recent'); // recent, name, frequency, category
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [bulkCategory, setBulkCategory] = useState('');

  useEffect(() => {
    loadRecentItems();
    loadCategories();
  }, [listId]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (showDeleteConfirm) {
          cancelDelete();
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showDeleteConfirm, onClose]);

  const loadRecentItems = async () => {
    try {
      setLoading(true);
      const items = await apiService.getRecentItems(listId);
      setRecentItems(items);
    } catch (error) {
      console.error('Error loading recent items:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      // Try to get user-wide categories first
      const response = await apiService.getUserCategories();
      const cats = Array.isArray(response) ? response : (response.categories || []);
      setCategories(cats);
      console.log('Loaded user categories:', cats);
    } catch (error) {
      console.error('Error loading user categories, falling back to list categories:', error);
      // Fallback to list-specific categories if user categories fail
      try {
        const cats = await apiService.getCategories(listId);
        // getCategories returns array directly, getUserCategories returns {categories: [...]}
        setCategories(Array.isArray(cats) ? cats : []);
        console.log('Loaded list categories as fallback:', cats);
      } catch (fallbackError) {
        console.error('Error loading fallback categories:', fallbackError);
        // Set some default categories if everything fails
        setCategories(['General', 'Food', 'Household', 'Personal Care']);
      }
    }
  };

  const handleUpdateCategory = async (itemId, newCategory) => {
    try {
      await apiService.updateItemCategory(listId, itemId, newCategory);
      setRecentItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, category: newCategory } : item
      ));
      setEditingItem(null);
      setEditCategory('');
    } catch (error) {
      console.error('Error updating category:', error);
      alert(t('errorUpdatingCategory', 'Error updating category'));
    }
  };

  const handleDeleteItem = async (itemId) => {
    setItemToDelete(itemId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await apiService.deleteItem(listId, itemToDelete);
      setRecentItems(prev => prev.filter(item => item.id !== itemToDelete));
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      alert(t('errorDeletingItem', 'Error deleting item'));
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setItemToDelete(null);
  };

  const filteredItems = recentItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Enhanced sorting with useMemo for performance
  const sortedAndFilteredItems = useMemo(() => {
    const filtered = recentItems.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'category':
          comparison = (a.category || 'General').localeCompare(b.category || 'General');
          break;
        case 'frequency':
          // Calculate frequency based on how many times item appears (mock data for now)
          const freqA = a.usageCount || 1;
          const freqB = b.usageCount || 1;
          comparison = freqA - freqB;
          break;
        case 'recent':
        default:
          const dateA = new Date(a.addedAt || 0);
          const dateB = new Date(b.addedAt || 0);
          comparison = dateA - dateB;
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [recentItems, searchTerm, sortBy, sortOrder]);

  // Statistics calculation
  const itemStats = useMemo(() => {
    const totalItems = recentItems.length;
    const categoryCounts = recentItems.reduce((acc, item) => {
      const cat = item.category || 'General';
      acc[cat] = (acc[cat] || 0) + 1;
      return acc;
    }, {});
    
    const mostUsedCategory = Object.entries(categoryCounts)
      .sort(([,a], [,b]) => b - a)[0];
    
    return {
      totalItems,
      categories: Object.keys(categoryCounts).length,
      mostUsedCategory: mostUsedCategory ? mostUsedCategory[0] : 'None',
      categoryCounts
    };
  }, [recentItems]);

  const startEditing = (item) => {
    setEditingItem(item.id);
    setEditCategory(item.category);
  };

  const cancelEditing = () => {
    setEditingItem(null);
    setEditCategory('');
  };

  const saveEdit = (itemId) => {
    handleUpdateCategory(itemId, editCategory);
  };

  // New functionality methods
  const handleQuickAdd = async (item) => {
    if (onAddToList) {
      try {
        await onAddToList({
          name: item.name,
          category: item.category || 'General'
        });
        // Optional: Show success feedback
      } catch (error) {
        console.error('Error adding item to list:', error);
        alert(t('errorAddingItem', 'Error adding item to list'));
      }
    }
  };

  const toggleSort = (newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  };

  const toggleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const selectAllVisible = () => {
    const visibleIds = sortedAndFilteredItems.map(item => item.id);
    setSelectedItems(new Set(visibleIds));
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  const bulkDelete = async () => {
    if (selectedItems.size === 0) return;
    
    try {
      const deletePromises = Array.from(selectedItems).map(itemId =>
        apiService.deleteItem(listId, itemId)
      );
      
      await Promise.all(deletePromises);
      
      setRecentItems(prev => prev.filter(item => !selectedItems.has(item.id)));
      setSelectedItems(new Set());
      setBulkMode(false);
    } catch (error) {
      console.error('Error bulk deleting items:', error);
      alert(t('errorBulkDelete', 'Error deleting selected items'));
    }
  };

  const bulkAddToList = async () => {
    if (selectedItems.size === 0 || !onAddToList) return;
    
    try {
      const selectedItemObjects = recentItems.filter(item => selectedItems.has(item.id));
      const addPromises = selectedItemObjects.map(item =>
        onAddToList({
          name: item.name,
          category: item.category || 'General'
        })
      );
      
      await Promise.all(addPromises);
      setSelectedItems(new Set());
      setBulkMode(false);
    } catch (error) {
      console.error('Error bulk adding items:', error);
      alert(t('errorBulkAdd', 'Error adding selected items'));
    }
  };

  const bulkUpdateCategory = async () => {
    if (selectedItems.size === 0 || !bulkCategory) return;
    
    try {
      const updatePromises = Array.from(selectedItems).map(itemId =>
        apiService.updateItem(listId, itemId, { category: bulkCategory })
      );
      
      await Promise.all(updatePromises);
      
      // Update the items in the state
      setRecentItems(prev => prev.map(item => 
        selectedItems.has(item.id) 
          ? { ...item, category: bulkCategory }
          : item
      ));
      
      setSelectedItems(new Set());
      setBulkCategory('');
      setBulkMode(false);
    } catch (error) {
      console.error('Error bulk updating categories:', error);
      alert(t('errorBulkUpdateCategory', 'Error updating categories for selected items'));
    }
  };

  // Handler methods for the new compact UI
  const handleBulkAddToList = () => {
    bulkAddToList();
  };

  const handleBulkDelete = () => {
    bulkDelete();
  };

  const handleBulkUpdateCategory = () => {
    if (bulkCategory) {
      bulkUpdateCategory();
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="recent-items-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="header-left">
            <h2>{t('recentItems', 'Recent Items')}</h2>
            <span className="item-count">({sortedAndFilteredItems.length})</span>
          </div>
          <div className="header-controls">
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="recent">{t('sortByRecent', 'Most Recent')}</option>
              <option value="name">{t('sortByName', 'Name')}</option>
              <option value="frequency">{t('sortByFrequency', 'Most Used')}</option>
              <option value="category">{t('sortByCategory', 'Category')}</option>
            </select>
            <button 
              onClick={() => setBulkMode(!bulkMode)}
              className={`bulk-toggle ${bulkMode ? 'active' : ''}`}
              title={t('bulkSelectionMode', 'Bulk Selection Mode')}
            >
              {t('bulkMode', 'Bulk')}
            </button>
          </div>
          <button onClick={onClose} className="close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="modal-content">
          {/* Bulk Actions Bar */}
          {bulkMode && selectedItems.size > 0 && (
            <div className="recent-bulk-actions">
              <span className="bulk-info">
                {selectedItems.size} {t('itemsSelected', 'items selected')}
              </span>
              <div className="bulk-controls">
                <select 
                  value={bulkCategory}
                  onChange={(e) => setBulkCategory(e.target.value)}
                  className="bulk-category-select"
                >
                  <option value="">{t('selectCategory', 'Select Category')}</option>
                  {getSelectableCategories(categories).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <button 
                  onClick={handleBulkUpdateCategory}
                  className="bulk-btn category"
                  disabled={!bulkCategory}
                >
                  {t('updateCategory', 'Update Category')}
                </button>
              </div>
              <div className="bulk-buttons">
                <button 
                  onClick={handleBulkAddToList}
                  className="bulk-btn add"
                >
                  {t('addToList', 'Add to List')}
                </button>
                <button 
                  onClick={handleBulkDelete}
                  className="bulk-btn delete"
                >
                  {t('delete', 'Delete')}
                </button>
                <button 
                  onClick={() => setSelectedItems(new Set())}
                  className="bulk-btn clear"
                >
                  {t('clearSelection', 'Clear')}
                </button>
              </div>
            </div>
          )}

          {/* Search */}
          <div className="search-container">
            <Search size={16} className="search-icon" />
            <input
              type="text"
                placeholder={t('searchItems', 'Search items...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

          {/* Recent Items List */}
          {loading ? (
            <div className="loading">{t('loading', 'Loading...')}</div>
          ) : (
            <div className="recent-items-list">
              {sortedAndFilteredItems.length === 0 ? (
                <div className="no-items">
                  {searchTerm ? 
                    t('noItemsFound', 'No items found') : 
                    t('noRecentItems', 'No recent items')
                  }
                </div>
              ) : (
                sortedAndFilteredItems.map(item => (
                  <div key={item.id} className={`recent-item ${bulkMode ? 'bulk-mode' : ''} ${selectedItems.has(item.id) ? 'selected' : ''}`}>
                    {bulkMode && (
                      <div className="item-checkbox">
                        <button 
                          onClick={() => toggleSelectItem(item.id)}
                          className="checkbox-btn"
                        >
                          {selectedItems.has(item.id) ? 
                            <CheckSquare size={16} /> : 
                            <Square size={16} />
                          }
                        </button>
                      </div>
                    )}
                    
                    {/* Item Name (Left) */}
                    <div className="item-name-section">
                      <h3 className="item-name">{item.name}</h3>
                    </div>
                    
                    {/* Category (Center) */}
                    <div className="category-display">
                      {editingItem === item.id ? (
                        <div className="category-edit">
                          <select
                            value={editCategory}
                            onChange={(e) => setEditCategory(e.target.value)}
                            className="category-select"
                          >
                            <option value="">{t('selectCategory', 'Select Category')}</option>
                            {getSelectableCategories(categories).map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                          <div className="edit-actions">
                            <button 
                              onClick={() => saveEdit(item.id)}
                              className="save-btn"
                              title={t('save', 'Save')}
                            >
                              <Save size={14} />
                            </button>
                            <button 
                              onClick={cancelEditing}
                              className="cancel-btn"
                              title={t('cancel', 'Cancel')}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="category-info">
                          <span className="category-badge">
                            {item.category || t('general', 'General')}
                          </span>
                          <button 
                            onClick={() => startEditing(item)}
                            className="edit-btn"
                            title={t('editCategory', 'Edit Category')}
                          >
                            <Edit3 size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                    
                    {/* Action Buttons (Right) */}
                    <div className="item-actions">
                      {onAddToList && !bulkMode && (
                        <button 
                          onClick={() => handleQuickAdd(item)}
                          className="quick-add-btn"
                          title={t('addToList', 'Add to List')}
                        >
                          <Plus size={18} />
                        </button>
                      )}
                      {!bulkMode && (
                        <button 
                          onClick={() => handleDeleteItem(item.id)}
                          className="delete-btn"
                          title={t('deleteItem', 'Delete Item')}
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('confirmDelete', 'Confirm Delete')}</h3>
            </div>
            <div className="modal-body">
              <p className="confirm-message">
                {t('confirmDeleteItem', 'Are you sure you want to delete this item?')}
              </p>
            </div>
            <div className="modal-footer">
              <button onClick={cancelDelete} className="btn btn-secondary">
                {t('cancel', 'Cancel')}
              </button>
              <button onClick={confirmDelete} className="btn btn-danger">
                {t('delete', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentItemsModal;
